/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, Grid, Alert, Typography,
  Table, TableBody, TableCell, TableContainer, TableRow, TablePagination,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SummarizeIcon from '@mui/icons-material/Summarize';
import Barcode from 'react-barcode';
import JsBarcode from 'jsbarcode';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';
import { printAdjustmentVoucher, printStockReport } from './printTemplates';

const TEAL = '#00798E';
const TH = {
  py: 1.5, px: 2, textTransform: 'uppercase', fontSize: '0.6rem',
  fontWeight: 700, color: '#999', borderBottom: '1.5px solid #f0f2f5',
  whiteSpace: 'nowrap', letterSpacing: '0.06em',
};
const td = (last) => ({
  py: 1.25, px: 2, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.05)', fontSize: '0.8rem',
});

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtNum = (v, dec = 0) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtExpiry = (v) => {
  if (!v) return '—';
  const [yr, mo] = String(v).split('-');
  return (yr && mo) ? `${MONTHS[parseInt(mo, 10) - 1]} ${yr}` : v;
};

const daysToExpiry = (dateStr) => {
  if (!dateStr) return Infinity;
  const diff = (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24);
  return Math.ceil(diff);
};

const expiryChip = (dateStr) => {
  const days = daysToExpiry(dateStr);
  const label = fmtExpiry(dateStr);
  if (days < 0) return <Chip label={`EXP ${label}`} size="small" color="error" sx={{ fontSize: '0.58rem', height: 18, fontWeight: 700 }} />;
  if (days <= 30) return <Chip label={`${label} (${days}d)`} size="small" color="warning" sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700 }} />;
  if (days <= 90) return <Chip label={`${label} (${days}d)`} size="small" color="info" sx={{ fontSize: '0.62rem', height: 18 }} />;
  return <Box sx={{ fontSize: '0.78rem', color: '#888' }}>{label}</Box>;
};

const TEAL_DARK = '#005469';

// ─── Inline stock row ──────────────────────────────────────
function StockRow({ label, base, sub, baseUnit, subUnit, color, bold = false }) {
  const fmt3 = (v) => {
    const n = Number(v || 0);
    return n % 1 === 0 ? n.toLocaleString('en-IN') : n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4, mb: 0.25 }}>
      <span style={{ fontSize: '0.6rem', color: '#bbb', width: 30, textAlign: 'right', flexShrink: 0, fontFamily: 'monospace' }}>{label}</span>
      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: bold ? 700 : 500, color, marginLeft: 4 }}>{fmt3(base)}</span>
      <span style={{ fontSize: '0.62rem', color, opacity: 0.75 }}>{baseUnit}</span>
      {sub !== null && sub !== undefined && (
        <>
          <span style={{ fontSize: '0.58rem', color: '#ddd', margin: '0 1px' }}>·</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: bold ? 700 : 400, color, opacity: 0.85 }}>{fmt3(sub)}</span>
          <span style={{ fontSize: '0.62rem', color, opacity: 0.65 }}>{subUnit}</span>
        </>
      )}
    </Box>
  );
}

function genBarcodeSvg(value) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, value, {
    format: 'CODE128', width: 1.6, height: 42, fontSize: 9,
    margin: 4, displayValue: true, textMargin: 2,
    background: '#ffffff', lineColor: '#000000',
  });
  return svg.outerHTML;
}

function PrintDialog({ target, onClose }) {
  const [qty, setQty] = useState(24);
  if (!target) return null;

  const handlePrint = () => {
    const svgStr = genBarcodeSvg(target.barcode);
    const name = (target.item?.name || '').substring(0, 38);
    const variant = target.variant?.variant_name ? ` · ${target.variant.variant_name}` : '';
    const meta = `Batch: ${target.batch_no || '—'} | Exp: ${fmtExpiry(target.expiry_date)} | MRP: ₹${Number(target.mrp || 0).toFixed(2)}`;
    const labels = Array.from({ length: qty }, () => `
      <div class="label">
        <div class="name">${name}${variant}</div>
        ${svgStr}
        <div class="meta">${meta}</div>
      </div>`).join('');

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>${name}</title>
      <style>
        @page { size: A5 landscape; margin: 8mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; background: #fff; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3mm; }
        .label { border: 0.5px solid #ccc; border-radius: 1.5mm; padding: 3mm 3mm 2mm; text-align: center; break-inside: avoid; }
        .name { font-size: 7.5pt; font-weight: 700; margin-bottom: 1mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .meta { font-size: 5.5pt; color: #555; margin-top: 1mm; }
        svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
      </style></head><body>
      <div class="grid">${labels}</div>
      <script>window.onload=function(){window.focus();window.print();};</script>
    </body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5,
        background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Print Barcodes</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #eee' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{target.item?.name || '—'}</Typography>
          {target.variant?.variant_name && (
            <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>{target.variant.variant_name}</Typography>
          )}
          <Typography sx={{ fontSize: '0.7rem', color: '#aaa', fontFamily: 'monospace', mt: 0.5 }}>{target.barcode}</Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Barcode value={target.barcode} width={1.2} height={36} fontSize={8} margin={3} displayValue background="transparent" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#555', whiteSpace: 'nowrap' }}>No. of copies:</Typography>
          <TextField
            type="number" size="small" value={qty}
            onChange={(e) => setQty(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 200, style: { textAlign: 'center', width: 60 } }}
            sx={{ '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.85rem' } }}
          />
          <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>(3 per row · A4)</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
        <Button size="small" variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
          sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, fontWeight: 700 }}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AdjustStockDialog({ target, ph, onClose, onAdjusted }) {
  const [direction, setDirection] = useState('ADD'); // ADD | REMOVE
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (target) { setDirection('ADD'); setQty(''); setNotes(''); setError(''); }
  }, [target]);

  if (!target) return null;

  const handleSave = async () => {
    const qtyNum = parseFloat(qty);
    if (!qtyNum || qtyNum <= 0) { setError('Enter a valid quantity'); return; }
    if (!notes.trim()) { setError('Reason is required'); return; }
    const qty_delta = direction === 'ADD' ? qtyNum : -qtyNum;
    setSaving(true); setError('');
    try {
      const res = await api.pharmacy.adjustStock({
        irm_pharm_inventory_id: target.id,
        qty_delta,
        notes: notes.trim(),
      });
      const updated = res.data || res;
      printAdjustmentVoucher({ qty_delta, notes: notes.trim(), balance_after: updated.qty_base_units }, target, ph);
      onAdjusted();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to adjust stock');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5,
        background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Adjust Stock</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #eee' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{target.item?.name || '—'}</Typography>
          {target.variant?.variant_name && (
            <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>{target.variant.variant_name}</Typography>
          )}
          <Typography sx={{ fontSize: '0.72rem', color: '#aaa', fontFamily: 'monospace', mt: 0.5 }}>
            Batch: {target.batch_no || '—'} · Current: {fmtNum(target.qty_base_units, 3)}
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.78rem' }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {['ADD', 'REMOVE'].map(d => (
            <Button key={d} size="small" fullWidth variant={direction === d ? 'contained' : 'outlined'}
              onClick={() => setDirection(d)}
              sx={{
                textTransform: 'none', fontWeight: 700,
                bgcolor: direction === d ? (d === 'ADD' ? '#2e7d32' : '#c62828') : 'transparent',
                color: direction === d ? '#fff' : (d === 'ADD' ? '#2e7d32' : '#c62828'),
                borderColor: d === 'ADD' ? '#2e7d32' : '#c62828',
                '&:hover': { bgcolor: direction === d ? (d === 'ADD' ? '#1b5e20' : '#8e0000') : (d === 'ADD' ? '#e8f5e9' : '#fdecea') },
              }}>
              {d === 'ADD' ? 'Add Stock' : 'Remove Stock'}
            </Button>
          ))}
        </Box>
        <TextField
          label="Quantity" type="number" size="small" fullWidth value={qty}
          onChange={(e) => setQty(e.target.value)}
          inputProps={{ min: 0, step: 'any' }}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { height: 40 } }}
        />
        <TextField
          label="Reason" size="small" fullWidth multiline minRows={2} value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Damaged in storage, physical count correction…"
        />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
        <Button size="small" variant="contained" disabled={saving} onClick={handleSave}
          sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, fontWeight: 700 }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save & Print Voucher'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SummaryCard({ label, value, sub, color, icon }) {
  return (
    <Card sx={{ p: 2.5, height: '100%', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: -8, right: -8, width: 60, height: 60,
        bgcolor: `${color}18`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Box sx={{ color, fontSize: 24 }}>{icon}</Box>
      </Box>
      <MDTypography variant="caption" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#aaa', fontWeight: 700 }}>
        {label}
      </MDTypography>
      <MDTypography variant="h4" sx={{ fontWeight: 800, color, mt: 0.5 }}>{value}</MDTypography>
      {sub && <MDTypography variant="caption" sx={{ fontSize: '0.68rem', color: '#aaa' }}>{sub}</MDTypography>}
    </Card>
  );
}

function InventoryDashboard() {
  const navigate = useNavigate();
  const { selectedPharmacy } = usePharmacy();
  const [stock, setStock] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lowStock, setLowStock] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [outOfStock, setOutOfStock] = useState([]);
  const [statsLoaded, setStatsLoaded] = useState(false);

  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [tab, setTab] = useState('all'); // all | low | expiring | out
  const [page, setPage] = useState(0);
  const [pageSize] = useState(30);
  const [printTarget, setPrintTarget] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);

  useEffect(() => {
    api.pharmacy.listLocations().then(r => setLocations(r.data || [])).catch(() => { });
  }, []);

  const loadStats = useCallback(() => {
    return Promise.all([
      api.pharmacy.getLowStock(),
      api.pharmacy.getExpiringStock(60),
      api.pharmacy.getOutOfStock(),
    ]).then(([low, exp, out]) => {
      setLowStock(low.data || []);
      setExpiring(exp.data || []);
      setOutOfStock(out.data || []);
      setStatsLoaded(true);
    }).catch(() => setStatsLoaded(true));
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const fetchStock = useCallback(async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      const res = await api.pharmacy.getStock({
        pharmacy_id: selectedPharmacy.id,
        location_id: locationFilter || undefined,
        limit: pageSize,
        offset: page * pageSize,
      });
      const data = res.data || [];
      const filtered = search
        ? data.filter(s =>
            (s.item?.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (s.batch_no || '').toLowerCase().includes(search.toLowerCase()))
        : data;
      setStock(filtered);
      setTotal(res.total || filtered.length);
    } catch { } finally { setLoading(false); }
  }, [locationFilter, page, pageSize, search]);

  const displayStock = (() => {
    if (tab === 'low') return lowStock;
    if (tab === 'expiring') return expiring;
    if (tab === 'out') return outOfStock;
    return stock;
  })();

  const kb = useListKeyboard({
    rowCount: displayStock.length,
    enabled: !adjustTarget && !printTarget,
    resetKey: tab,
    onOpen: (i) => { if (displayStock[i]) setAdjustTarget(displayStock[i]); },
  });

  useEffect(() => { if (tab === 'all') fetchStock(); }, [fetchStock, tab]);

  const refreshAfterAdjustment = () => {
    loadStats();
    if (tab === 'all') fetchStock();
  };

  const expiredCount = expiring.filter(s => daysToExpiry(s.expiry_date) < 0).length;
  const soonCount = expiring.filter(s => { const d = daysToExpiry(s.expiry_date); return d >= 0 && d <= 30; }).length;

  const REPORT_TITLE = { all: 'Current Stock Report', low: 'Low Stock Report', expiring: 'Expiring Stock Report', out: 'Out of Stock Report' };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {!selectedPharmacy && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.85rem' }}>
            Please select a pharmacy from the ribbon on the right edge of the screen to view inventory.
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <MDTypography variant="h5" fontWeight="bold">Inventory</MDTypography>
          <Button
            size="small" variant="outlined"
            startIcon={<TuneIcon />}
            sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL }}
            onClick={() => navigate('/pharmacy/inward/new')}
          >
            New GRN
          </Button>
        </Box>

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={2.4}>
            <SummaryCard
              label="Total SKUs" value={statsLoaded ? fmtNum(stock.length) : '—'}
              color={TEAL} icon={<InventoryIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <SummaryCard
              label="Out of Stock" value={statsLoaded ? fmtNum(outOfStock.length) : '—'}
              sub="Zero qty batches" color="#b71c1c"
              icon={<ErrorOutlineIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <SummaryCard
              label="Low Stock" value={statsLoaded ? fmtNum(lowStock.length) : '—'}
              sub="Below reorder level" color="#f57c00"
              icon={<WarningAmberIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <SummaryCard
              label="Expiring ≤30d" value={statsLoaded ? fmtNum(soonCount) : '—'}
              sub="Needs attention" color="#e65100"
              icon={<ErrorOutlineIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <SummaryCard
              label="Expired" value={statsLoaded ? fmtNum(expiredCount) : '—'}
              sub="Write-off pending" color="#d32f2f"
              icon={<ErrorOutlineIcon fontSize="inherit" />}
            />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, borderBottom: '2px solid #f0f0f0', pb: 0 }}>
          {[
            { key: 'all', label: 'All Stock' },
            { key: 'out', label: `Out of Stock (${outOfStock.length})` },
            { key: 'low', label: `Low Stock (${lowStock.length})` },
            { key: 'expiring', label: `Expiring (${expiring.length})` },
          ].map(t => (
            <Button
              key={t.key}
              size="small"
              onClick={() => { setTab(t.key); setPage(0); }}
              sx={{
                textTransform: 'none', fontWeight: tab === t.key ? 700 : 400,
                color: tab === t.key ? TEAL : '#888',
                borderBottom: tab === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
                borderRadius: 0, px: 2, py: 1, mb: '-2px',
                fontSize: '0.82rem',
              }}
            >
              {t.label}
            </Button>
          ))}
          <Button
            size="small" startIcon={<SummarizeIcon />}
            onClick={() => printStockReport(displayStock, REPORT_TITLE[tab], selectedPharmacy)}
            disabled={!displayStock.length}
            sx={{ ml: 'auto', mb: '-2px', textTransform: 'none', color: TEAL, fontSize: '0.78rem', fontWeight: 600 }}
          >
            Print Report
          </Button>
        </Box>

        {/* Filters */}
        {tab === 'all' && (
          <Card sx={{ mb: 2 }}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                {...kb.searchProps}
                size="small" placeholder="Search item / batch...  ( / to focus )"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#ccc' }} /></InputAdornment>,
                }}
                sx={{ minWidth: 250, '& .MuiOutlinedInput-root': { height: 34 } }}
              />
              <FormControl size="small" sx={{ minWidth: 145 }}>
                <InputLabel>Location</InputLabel>
                <Select value={locationFilter} label="Location"
                  onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}
                  sx={{ height: 34 }}>
                  <MenuItem value="">All Locations</MenuItem>
                  {locations.map(l => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Card>
        )}

        {/* Stock table */}
        <Card>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableRow>
                {['Item', 'Batch', 'Expiry', 'Location', 'Stock', 'MRP', 'Purchase Price', 'Barcode', ''].map((h, i) => (
                  <TableCell key={i} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={24} sx={{ color: TEAL }} />
                    </TableCell>
                  </TableRow>
                ) : displayStock.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, color: '#bbb', fontSize: '0.82rem' }}>
                      {tab === 'all' ? 'No stock records found' : tab === 'out' ? 'No out-of-stock items' : tab === 'low' ? 'No low stock items' : 'No expiring items'}
                    </TableCell>
                  </TableRow>
                ) : displayStock.map((s, i) => {
                  const last = i === displayStock.length - 1;
                  const days = daysToExpiry(s.expiry_date);
                  const rowBg = days < 0 ? 'rgba(211,47,47,0.04)' : days <= 30 ? 'rgba(230,81,0,0.04)' : 'transparent';
                  return (
                    <TableRow key={s.id} hover {...kb.getRowProps(i)} sx={{ bgcolor: rowBg, cursor: 'pointer', ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}) }}>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontWeight: 500 }}>{s.item?.name || '—'}</Box>
                        {s.variant && <Box sx={{ fontSize: '0.7rem', color: '#aaa' }}>{s.variant?.variant_name}</Box>}
                        <Box sx={{ fontSize: '0.68rem', color: '#bbb', fontFamily: 'monospace' }}>{s.item?.item_code}</Box>
                      </TableCell>
                      <TableCell sx={{ ...td(last), fontFamily: 'monospace', fontWeight: 600, color: '#444' }}>
                        {s.batch_no}
                      </TableCell>
                      <TableCell sx={td(last)}>{expiryChip(s.expiry_date)}</TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontSize: '0.78rem', color: '#555' }}>{s.location?.name || '—'}</Box>
                      </TableCell>
                      <TableCell sx={{ ...td(last), minWidth: 160, py: 1 }}>
                        {(() => {
                          const item = s.item || {};
                          const grnItem = s.grn_item || null;
                          const subPerBase = Number(item.sub_units_per_base || 1);
                          const hasSubUnit = item.is_sub_unit_sellable === 'Y' && item.sub_unit && subPerBase > 1;
                          const baseUnitShort = item.base_unit?.short_name || item.base_unit?.name || 'U';
                          const subUnitShort = item.sub_unit?.short_name || item.sub_unit?.name || '';
                          const availBase = Number(s.qty_base_units);
                          const isExpired = daysToExpiry(s.expiry_date) < 0;

                          let recvdBase = null;
                          if (grnItem) {
                            recvdBase = Number(grnItem.qty_received || 0) + Number(grnItem.qty_free || 0);
                          }
                          const soldBase = recvdBase !== null ? Math.max(0, recvdBase - availBase) : null;

                          const availColor = isExpired ? '#c62828' : availBase <= 0 ? '#c62828' : '#2e7d32';

                          return (
                            <Box>
                              {recvdBase !== null && (
                                <StockRow
                                  label="Rcvd" base={recvdBase}
                                  sub={hasSubUnit ? recvdBase * subPerBase : null}
                                  baseUnit={baseUnitShort} subUnit={subUnitShort}
                                  color="#555"
                                />
                              )}
                              {soldBase !== null && (
                                <StockRow
                                  label="Sold" base={soldBase}
                                  sub={hasSubUnit ? soldBase * subPerBase : null}
                                  baseUnit={baseUnitShort} subUnit={subUnitShort}
                                  color="#e57373"
                                />
                              )}
                              <StockRow
                                label={isExpired ? 'Exprd' : 'Avail'}
                                base={availBase}
                                sub={hasSubUnit ? availBase * subPerBase : null}
                                baseUnit={baseUnitShort} subUnit={subUnitShort}
                                color={availColor} bold
                              />
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={td(last)}>₹{fmtNum(s.mrp, 2)}</TableCell>
                      <TableCell sx={td(last)}>₹{fmtNum(s.purchase_price, 2)}</TableCell>
                      <TableCell sx={{ ...td(last) }}>
                        {s.barcode ? (
                          <>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#aaa', mb: 0.5 }}>
                              {s.barcode}
                            </Box>
                            <Barcode
                              value={s.barcode}
                              width={0.8}
                              height={20}
                              fontSize={0}
                              margin={0}
                              displayValue={false}
                              background="transparent"
                            />
                          </>
                        ) : (
                          <Box sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#ddd' }}>—</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...td(last), whiteSpace: 'nowrap' }}>
                        {Number(s.qty_base_units) > 0 && (
                          <Tooltip title="Stock OK">
                            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#4caf50', mr: 0.5 }} />
                          </Tooltip>
                        )}
                        {s.barcode && (
                          <Tooltip title="Print barcode labels">
                            <IconButton size="small" onClick={() => setPrintTarget(s)}
                              sx={{ p: 0.4, color: TEAL, '&:hover': { bgcolor: `${TEAL}14` } }}>
                              <PrintIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Adjust stock">
                          <IconButton size="small" onClick={() => setAdjustTarget(s)}
                            sx={{ p: 0.4, color: '#6d4c41', '&:hover': { bgcolor: 'rgba(109,76,65,0.08)' } }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {tab === 'all' && (
            <TablePagination
              component="div" count={total} page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize} rowsPerPageOptions={[pageSize]}
            />
          )}
        </Card>
      </MDBox>
      <Footer />
      <PrintDialog target={printTarget} onClose={() => setPrintTarget(null)} />
      <AdjustStockDialog target={adjustTarget} ph={selectedPharmacy} onClose={() => setAdjustTarget(null)} onAdjusted={refreshAfterAdjustment} />
    </DashboardLayout>
  );
}

export default InventoryDashboard;
